import { z } from "zod";

import { HelpChatRole } from "./help-conversation";

/**
 * Support-ticket shapes shared between the API (`apps/api/src/ticket/`) and the web app
 * (`/app/support` for customers, `/app/tickets` for staff). The data model is
 * docs/superpowers/specs/2026-09-02-internal-ticket-system-design.md §3; the limits below are
 * the single definition the API validates against and the web drop zone displays.
 */

export enum TicketStatus {
	OPEN = "OPEN",
	WAITING_ON_CUSTOMER = "WAITING_ON_CUSTOMER",
	WAITING_ON_US = "WAITING_ON_US",
	RESOLVED = "RESOLVED",
	CLOSED = "CLOSED",
}

export enum TicketCategory {
	BUG = "BUG",
	QUESTION = "QUESTION",
	FEATURE_REQUEST = "FEATURE_REQUEST",
	BILLING = "BILLING",
	DATA_QUALITY = "DATA_QUALITY",
	/** A piece of work the desk takes on rather than a customer's report. Staff-only. */
	TASK = "TASK",
}

/**
 * Categories only staff may file or re-file into. A customer never sees them in a picker and the
 * customer create route refuses them, the same rule `TicketPriority` follows — but the pill still
 * renders for a customer whose ticket staff re-filed, so every category keeps a label.
 */
export const STAFF_ONLY_TICKET_CATEGORIES: readonly TicketCategory[] = [TicketCategory.TASK];

/** What the customer form offers and the customer filter lists. */
export const CUSTOMER_TICKET_CATEGORIES: readonly TicketCategory[] = Object.values(
	TicketCategory
).filter((category) => !STAFF_ONLY_TICKET_CATEGORIES.includes(category));

export function isCustomerTicketCategory(category: TicketCategory): boolean {
	return !STAFF_ONLY_TICKET_CATEGORIES.includes(category);
}

/** Staff-only (spec §10): never rendered to a customer, never accepted from one. */
export enum TicketPriority {
	LOW = "LOW",
	NORMAL = "NORMAL",
	HIGH = "HIGH",
	URGENT = "URGENT",
}

export enum TicketSource {
	CHAT_BOT = "CHAT_BOT",
	FORM = "FORM",
	STAFF = "STAFF",
	/** Arrived as a mail to support@signalist.io (PREC-3978). */
	EMAIL = "EMAIL",
	/** Opened by Signalist itself — a list run reporting a source that failed (PREC-3996). */
	SYSTEM = "SYSTEM",
}

export enum TicketLinkedEntityType {
	LIST = "LIST",
	CAMPAIGN = "CAMPAIGN",
	LEAD = "LEAD",
	CONVERSATION = "CONVERSATION",
}

/**
 * The thread is the history (spec §3): the description is the first MESSAGE, the help-chat
 * transcript one TRANSCRIPT row, and every status change one STATUS_CHANGE row.
 */
export enum TicketCommentKind {
	MESSAGE = "MESSAGE",
	TRANSCRIPT = "TRANSCRIPT",
	STATUS_CHANGE = "STATUS_CHANGE",
}

/** Statuses in which somebody still owes the ticket an action. */
export const TICKET_OPEN_STATUSES = [
	TicketStatus.OPEN,
	TicketStatus.WAITING_ON_CUSTOMER,
	TicketStatus.WAITING_ON_US,
] as const;

export const TICKET_SUBJECT_MAX_LENGTH = 200;
export const TICKET_MESSAGE_MAX_LENGTH = 8000;
/** A RESOLVED ticket the customer has not touched for this long is CLOSED by the daily cron. */
export const TICKET_AUTO_CLOSE_AFTER_DAYS = 7;

export const TICKET_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
/** Per comment, or per ticket body for attachments uploaded with no comment. */
export const TICKET_ATTACHMENT_MAX_FILES = 5;
export const TICKET_ATTACHMENT_ALLOWED_MIME_TYPES = [
	"image/png",
	"image/jpeg",
	"image/gif",
	"image/webp",
	"text/csv",
	"text/plain",
	"application/pdf",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
] as const;
export type TicketAttachmentMimeType = (typeof TICKET_ATTACHMENT_ALLOWED_MIME_TYPES)[number];

/**
 * How many raw bytes of attachments one outgoing mail may carry (PREC-4088).
 *
 * Mailjet caps a v3.1 send at ~15 MB for the whole message, and an attachment travels as
 * `Base64Content` — which inflates by 4/3. So the per-comment limits above allow more than a mail
 * can hold: 5 files x 5 MB is 25 MB raw, 33 MB encoded. 10 MB raw encodes to ~13.3 MB, leaving
 * room for the HTML body and the JSON envelope without approaching the cap.
 *
 * Files beyond this budget are NOT dropped silently — the mail names them, so the recipient can
 * ask for them rather than never learning they existed.
 */
export const TICKET_MAIL_ATTACHMENT_TOTAL_MAX_BYTES = 10 * 1024 * 1024;

export const ticketStatusSchema = z.enum(TicketStatus);
export const ticketCategorySchema = z.enum(TicketCategory);
export const ticketPrioritySchema = z.enum(TicketPriority);
export const ticketSourceSchema = z.enum(TicketSource);
export const ticketLinkedEntityTypeSchema = z.enum(TicketLinkedEntityType);
export const ticketCommentKindSchema = z.enum(TicketCommentKind);

export const ticketLinkedEntitySchema = z.object({
	type: ticketLinkedEntityTypeSchema,
	id: z.number().int().positive(),
});
export type TicketLinkedEntity = z.infer<typeof ticketLinkedEntitySchema>;

/** Route *pattern* only, never a resolved URL with ids — same rule as the help chat. */
export const ticketPageContextSchema = z.object({
	routePattern: z.string().max(512).optional(),
	label: z.string().max(256).optional(),
});
export type TicketPageContext = z.infer<typeof ticketPageContextSchema>;

/** One turn of the help chat that preceded the ticket; stored as JSON in a TRANSCRIPT comment. */
export const ticketTranscriptEntrySchema = z.object({
	role: z.enum(HelpChatRole),
	content: z.string().max(TICKET_MESSAGE_MAX_LENGTH),
});
export type TicketTranscriptEntry = z.infer<typeof ticketTranscriptEntrySchema>;
export const ticketTranscriptSchema = z.array(ticketTranscriptEntrySchema);

/** Body of a STATUS_CHANGE comment. `from` is null for the row written at creation. */
export const ticketStatusChangeSchema = z.object({
	from: ticketStatusSchema.nullable(),
	to: ticketStatusSchema,
	reason: z.string().max(200).nullable(),
});
export type TicketStatusChange = z.infer<typeof ticketStatusChangeSchema>;

/**
 * The slice of a user a ticket response carries — enough to render a name, an avatar and, via
 * `companyId`, whether the author is Signalist staff (a company other than the ticket's).
 */
export const ticketUserRefSchema = z.object({
	id: z.number(),
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	email: z.string(),
	companyId: z.number().nullable(),
});
export type TicketUserRef = z.infer<typeof ticketUserRefSchema>;

/** Attachment metadata — the blob itself only ever travels through the download route. */
export const ticketAttachmentMetaSchema = z.object({
	id: z.number(),
	ticketId: z.number(),
	commentId: z.number().nullable(),
	uploaderUserId: z.number(),
	fileName: z.string(),
	mimeType: z.string(),
	sizeBytes: z.coerce.number(),
	createdAt: z.string(),
});
export type TicketAttachmentMeta = z.infer<typeof ticketAttachmentMetaSchema>;

export const ticketCommentSchema = z.object({
	id: z.number(),
	ticketId: z.number(),
	/** Null for system rows (auto-close cron). */
	authorUserId: z.number().nullable(),
	author: ticketUserRefSchema.nullable(),
	/** The mail author when there is no user row to point at (PREC-3978, exposed in PREC-4002). */
	externalAuthorEmail: z.string().max(320).nullable(),
	kind: ticketCommentKindSchema,
	/** MESSAGE: the text. TRANSCRIPT / STATUS_CHANGE: JSON per the schemas above. */
	body: z.string(),
	isInternal: z.boolean(),
	attachments: z.array(ticketAttachmentMetaSchema),
	createdAt: z.string(),
});
export type TicketComment = z.infer<typeof ticketCommentSchema>;

/** What every reader of a ticket sees. Priority is staff-only and lives on `staffTicketSchema`. */
export const ticketSchema = z.object({
	id: z.number(),
	companyId: z.number(),
	createdByUserId: z.number().int().positive().nullable(),
	/** Set when the filer is not a Signalist user — a mail from an unknown address (PREC-3978). */
	externalSenderEmail: z.string().max(320).nullable(),
	externalSenderName: z.string().max(255).nullable(),
	createdBy: ticketUserRefSchema.nullable(),
	filedByStaffUserId: z.number().nullable(),
	assigneeUserId: z.number().nullable(),
	subject: z.string(),
	category: ticketCategorySchema,
	status: ticketStatusSchema,
	source: ticketSourceSchema,
	routePattern: z.string().nullable(),
	pageLabel: z.string().nullable(),
	linkedEntityType: ticketLinkedEntityTypeSchema.nullable(),
	linkedEntityId: z.number().nullable(),
	resolvedAt: z.string().nullable(),
	closedAt: z.string().nullable(),
	lastCustomerActivityAt: z.string().nullable(),
	lastStaffActivityAt: z.string().nullable(),
	attachmentCount: z.coerce.number(),
	createdAt: z.string(),
	updatedAt: z.string(),
});
export type Ticket = z.infer<typeof ticketSchema>;

export const ticketDetailSchema = ticketSchema.extend({
	comments: z.array(ticketCommentSchema),
	attachments: z.array(ticketAttachmentMetaSchema),
});
export type TicketDetail = z.infer<typeof ticketDetailSchema>;

/**
 * Response of `POST tickets/:id/comments`: the ticket after the reply plus the MESSAGE row it wrote,
 * so the web can attach files to that comment (PREC-3938).
 */
export const ticketReplyResponseSchema = ticketSchema.extend({ comment: ticketCommentSchema });
export type TicketReplyResponse = z.infer<typeof ticketReplyResponseSchema>;

export const staffTicketSchema = ticketSchema.extend({
	priority: ticketPrioritySchema,
	companyName: z.string().nullable(),
	assignee: ticketUserRefSchema.nullable(),
});
export type StaffTicket = z.infer<typeof staffTicketSchema>;

export const staffTicketDetailSchema = staffTicketSchema.extend({
	comments: z.array(ticketCommentSchema),
	attachments: z.array(ticketAttachmentMetaSchema),
});
export type StaffTicketDetail = z.infer<typeof staffTicketDetailSchema>;

/**
 * Response of `POST tickets/admin/:id/comments`: the ticket after the reply or note plus the MESSAGE
 * row it wrote, so the staff composer can attach files to that comment — the staff twin of
 * `ticketReplyResponseSchema` (PREC-3940 decision 1).
 */
export const staffTicketReplyResponseSchema = staffTicketSchema.extend({
	comment: ticketCommentSchema,
});
export type StaffTicketReplyResponse = z.infer<typeof staffTicketReplyResponseSchema>;

/**
 * Response of `POST tickets/admin/:id/comments/:commentId/dispatch` — the step that actually mails
 * a staff reply to an external sender, once the composer has finished uploading its files
 * (PREC-4088).
 *
 * `sent: false` is an ordinary answer, not a failure: this call already happened, or the reply is
 * one no support mail speaks for (an internal note, a matched customer who hears through the
 * notification pipeline, a ticket that did not arrive by mail).
 */
export const staffTicketReplyDispatchResponseSchema = z.object({
	sent: z.boolean(),
	attachmentCount: z.number().int().min(0),
});
export type StaffTicketReplyDispatchResponse = z.infer<
	typeof staffTicketReplyDispatchResponseSchema
>;

/** Body of `POST tickets` (customer form / help chat) and the customer part of `POST tickets/admin`. */
export const createTicketRequestSchema = z.object({
	subject: z.string().trim().min(1).max(TICKET_SUBJECT_MAX_LENGTH),
	category: ticketCategorySchema,
	message: z.string().trim().min(1).max(TICKET_MESSAGE_MAX_LENGTH),
	pageContext: ticketPageContextSchema.optional(),
	transcript: ticketTranscriptSchema.optional(),
	linkedEntity: ticketLinkedEntitySchema.optional(),
});
export type CreateTicketRequest = z.infer<typeof createTicketRequestSchema>;

/** Body of `POST tickets/admin`: the customer fields plus which customer the ticket is filed for. */
export const createStaffTicketRequestSchema = createTicketRequestSchema.extend({
	companyId: z.number().int().positive(),
	/** Becomes `createdByUserId`; must be a user of `companyId`. */
	onBehalfOfUserId: z.number().int().positive(),
});
export type CreateStaffTicketRequest = z.infer<typeof createStaffTicketRequestSchema>;

/** Body of `POST tickets/admin/:id/comments`. */
export const createStaffTicketCommentRequestSchema = z.object({
	message: z.string().trim().min(1).max(TICKET_MESSAGE_MAX_LENGTH),
	/** An internal note: staff-only, never changes the status. Default false. */
	isInternal: z.boolean().optional(),
	/** "Then set:" on the composer — the status to land in with this comment; omitted = the implicit transition. */
	status: ticketStatusSchema.optional(),
});
export type CreateStaffTicketCommentRequest = z.infer<typeof createStaffTicketCommentRequestSchema>;

/** Body of `PATCH tickets/admin/:id` — every field optional; `assigneeUserId: null` unassigns. */
export const updateStaffTicketRequestSchema = z.object({
	status: ticketStatusSchema.optional(),
	assigneeUserId: z.number().int().positive().nullable().optional(),
	priority: ticketPrioritySchema.optional(),
	category: ticketCategorySchema.optional(),
});
export type UpdateStaffTicketRequest = z.infer<typeof updateStaffTicketRequestSchema>;

/** One row of `GET tickets/admin/counts`: the open-ticket cell on a Support Dashboard company row. */
export const companyOpenTicketCountSchema = z.object({
	companyId: z.number(),
	/** OPEN + WAITING_ON_CUSTOMER + WAITING_ON_US. */
	openCount: z.number(),
	/** The WAITING_ON_US share of `openCount` — the red pill (PREC-3939 decision 8). */
	waitingOnUsCount: z.number(),
});
export type CompanyOpenTicketCount = z.infer<typeof companyOpenTicketCountSchema>;

/** First-reply median window of `GET tickets/admin/stats`, in days. */
export const STAFF_TICKET_STATS_REPLY_WINDOW_DAYS = 30;

/**
 * `GET tickets/admin/stats` — the triage strip, the tab counts and the sidebar badge in one read
 * (PREC-3939 decision 2). Whole-queue numbers, never narrowed by a filter; `tabs.mine` is the
 * caller's. Auto-close dates are `resolvedAt + TICKET_AUTO_CLOSE_AFTER_DAYS`.
 */
export const staffTicketStatsSchema = z.object({
	waitingOnUs: z.object({
		count: z.number(),
		/** The longest-waiting WAITING_ON_US ticket's last customer activity; null when none. */
		oldestCustomerActivityAt: z.string().nullable(),
	}),
	/** Status OPEN: nobody has touched it yet. */
	openUntriaged: z.number(),
	/** No assignee, over the three open statuses. */
	unassigned: z.number(),
	waitingOnCustomer: z.number(),
	resolvedAwaitingClose: z.object({
		count: z.number(),
		earliestAutoCloseAt: z.string().nullable(),
		latestAutoCloseAt: z.string().nullable(),
	}),
	/** Over tickets created in the last STAFF_TICKET_STATS_REPLY_WINDOW_DAYS; null when none replied. */
	medianFirstReplyMinutes: z.number().nullable(),
	tabs: z.object({
		needsAction: z.number(),
		mine: z.number(),
		unassigned: z.number(),
		allOpen: z.number(),
		resolved: z.number(),
		closed: z.number(),
	}),
});
export type StaffTicketStats = z.infer<typeof staffTicketStatsSchema>;

/** `GET tickets/admin/assignees`: everyone who may be set as a ticket's assignee. */
export const staffAssigneeListSchema = z.array(ticketUserRefSchema);

/** Mirrors the API's `PageMetaDTO`. */
export const ticketPageMetaSchema = z.object({
	page: z.number(),
	limit: z.number(),
	itemCount: z.number(),
	pageCount: z.number(),
	hasPreviousPage: z.boolean(),
	hasNextPage: z.boolean(),
});

export const ticketListResponseSchema = z.object({
	data: z.array(ticketSchema),
	meta: ticketPageMetaSchema,
});
export type TicketListResponse = z.infer<typeof ticketListResponseSchema>;

export const staffTicketListResponseSchema = z.object({
	data: z.array(staffTicketSchema),
	meta: ticketPageMetaSchema,
});
export type StaffTicketListResponse = z.infer<typeof staffTicketListResponseSchema>;
